import { useState } from 'react';
import { EmptyState, InlineError, Skeleton } from '../../components/ui';
import { useEffectiveSupplierOrg } from '../supplierOrganizations/MimicMode';
import { slaRemaining, slaTone, todayNow } from '../cgOrders/sla';
import { useSupplierOrders } from './supplierOrders';
import type { SupplierOrder } from './supplierOrders';
import { jobBoard } from './jobBoard';
import type { JobBoardCard } from './jobBoard';
import { stageCounterLabel } from '../cgOrders/stageClock';
import { JobOrderPanel } from './JobOrderPanel';

/**
 * The factory's board.
 *
 * ── What this replaces ─────────────────────────────────────────────────────
 *
 * Three headed lists — Needs you, With TAP, Completed — where every card
 * carried its own Accept button, its own revision form, its own tracking form
 * and its own copy of the line items. Four jobs meant four expanded forms
 * stacked down a page, and the decision to accept a PO was offered on a
 * summary row by somebody who had not seen the artwork.
 *
 * Now: nine columns, one line per job, and every action on the job's own page.
 * A card is a pointer, not a workspace. That is the whole change, and it is
 * why this file lost two thirds of its length.
 *
 * ── Everything visible at once ─────────────────────────────────────────────
 *
 * The columns share the width equally and wrap onto a second row on a narrow
 * screen rather than scrolling sideways. A board you have to scroll to read is
 * a board that cannot answer "where is everything" — which is its only job.
 */

export function PortalOrders() {
  const organizationId = useEffectiveSupplierOrg();
  const orders = useSupplierOrders(organizationId);
  // Which job is open, if any. Local rather than routed: the portal is one
  // page and a route would have to survive the mimic-mode org switch too.
  const [openJob, setOpenJob] = useState<string | null>(null);
  // Archived work is hidden by default — CLOSED alone, since 20261120000100.
  //
  // Kyu: "I marked the test order to Delivered in TAP side... but when I go to
  // supplier side, the job order is gone." It was behind this toggle, which
  // used to count Delivered as finished. That is the batch-37 defect on the
  // other board: TAP's pipeline had the same collapse and was split into
  // is_open (still being worked, governs stall alerts) and is_archived (out of
  // sight, Closed alone). The factory's board kept its own hardcoded copy of
  // the question and answered it differently, so one job was visible on one
  // board and hidden on the other.
  //
  // The rule now travels on the row rather than being restated here, because
  // restating it is what caused this.
  const [showArchived, setShowArchived] = useState(false);

  if (organizationId === '' || orders.isPending) {
    return <Skeleton lines={4} label="Loading jobs" />;
  }
  if (orders.isError) {
    return <InlineError>Jobs could not be loaded.</InlineError>;
  }

  // One job, owning the screen. The board answers "where is everything"; this
  // answers "what am I being asked to build" — and the two questions have no
  // business sharing a viewport.
  if (openJob !== null) {
    return <JobOrderPanel orderId={openJob} onBack={() => setOpenJob(null)} />;
  }

  const rows = orders.data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No jobs yet"
        description="A job appears here once TAP issues the PO for a design you acknowledged."
      />
    );
  }

  const archived = rows.filter((row) => row.is_archived);
  const columns = jobBoard(showArchived ? rows : rows.filter((row) => !row.is_archived));

  return (
    <>
      {archived.length === 0 ? null : (
        <label className="job-board-toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
          {/* TAP's pipeline says "Show closed orders". One vocabulary: a
              factory and a coordinator on a call should be describing the
              same control. */}
          Show closed jobs
          <span className="job-board-toggle-count">{archived.length}</span>
        </label>
      )}
      <div className="job-board" role="list" aria-label="Job orders by stage">
        {columns.map((column) => (
          <section key={column.key} className="job-board-column" role="listitem">
            <header className="job-board-column-head">
              <span className="job-board-column-label">{column.label}</span>
              <span className="job-board-column-count">{column.cards.length}</span>
            </header>
            {/* An empty column is drawn, not hidden — see jobBoard.ts. It stays
              quiet rather than announcing its emptiness nine times.

              The cards sit in a body of their own so a long column scrolls
              INSIDE itself and the stage name stays put — the same fix the
              Deals board got on 1 September, for the same reason: a column of
              twelve jobs used to scroll the whole page and take the other
              eight columns with it. */}
            <div className="job-board-column-body">
              {column.cards.map((card) => (
                <JobCard key={card.orderId} card={card} onOpen={setOpenJob} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

/**
 * Delivered and Closed. Read from the STATUS rather than from the board column
 * so a job that lands in the Delivered column for another reason is not hidden
 * by accident.
 */
/**
 * One job, as a line you can read in a glance and click.
 *
 * Carries the four facts that decide whether to open it: which job, how long
 * it has been sitting here, whether the sample clock is running out, and when
 * it is due to ship. No line items, no buttons, no forms.
 */
function JobCard({
  card,
  onOpen,
}: {
  card: JobBoardCard<SupplierOrder>;
  onOpen: (orderId: string) => void;
}) {
  const order = card.order;
  // From status_changed_at rather than the full history: the board needs one
  // number per card and the history costs a query per job. The timeline inside
  // the job is where every stage is measured.
  const counter = stageCounterLabel(
    [
      {
        from_status: null,
        to_status: order.status,
        entered_at: order.status_changed_at,
        is_estimate: false,
      },
    ],
    todayNow(),
  );
  // Whichever promise is live. Only one clock ever runs: the sample window
  // closes at the moment the production window opens.
  const sla =
    order.production_clock_started_at === null
      ? slaRemaining(order.sample_clock_started_at, order.sample_due_days, todayNow())
      : slaRemaining(order.production_clock_started_at, order.production_due_days, todayNow());

  return (
    <button type="button" className="job-board-card" onClick={() => onOpen(order.order_id)}>
      {/* EVERY slot is drawn, every time. Four of these facts are optional,
          and when each one collapsed on absence a column of cards ran from
          one line to five — the exact unevenness Kyu named on the Deals board
          ("how each boxes have same dimensions"). A slot with nothing to say
          holds its line and says nothing; the card stays the height of every
          other card. */}
      <span className="job-board-card-no">{card.orderNo}</span>
      {/* Whose move it is, in this side's words. The shared model says
          'supplier'; only the portal turns that into "you". On its own line
          rather than beside the number: at nine columns across a laptop a
          column is ~150px, and side by side the number wrapped and "Your move"
          became "Your m…" — measured, in the first render of this change. */}
      <span
        className={
          card.turn === 'supplier'
            ? 'job-board-card-turn job-board-card-turn--you'
            : 'job-board-card-turn'
        }
      >
        {card.turn === null ? '' : card.turn === 'supplier' ? 'Your move' : 'With TAP'}
      </span>
      <span className="job-board-card-days">{counter ?? ''}</span>
      <span className={`job-board-card-sla job-board-card-sla--${slaTone(sla.state)}`}>
        {order.supplier_confirmed_at === null ? '' : sla.label}
      </span>
      <span className="job-board-card-ship">
        {order.target_ship_date === null ? '' : `Ship by ${order.target_ship_date}`}
      </span>
    </button>
  );
}
